import { Box, Typography } from '@mui/material';
import {
  LessonPlayerContextModel,
  LessonPlayerContextProvider
} from '../models/LessonPlayerContext';
import React, { Suspense, useEffect, useState } from 'react';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams
} from 'react-router-dom';

import { ApiPortfolioState } from '../models/ApiPortfolioState';
import AuthService from '../../shared/services/AuthService';
import CourseDialog from './CourseDialog';
import { CustomLoadingProgress } from '../../shared/components/CustomLoadingProgress';
import Grid from '@mui/material/Unstable_Grid2';
import LessonLaunchParamsV2 from '../models/LessonLaunchParamsV2';
import LessonPlayerEmbedded from './LessonPlayerEmbedded';
import PathwayDialog from './PathwayDialog';
import UserDashboard from './UserDashboard';
import useLessons from '../../content/hooks/useLessons';
import usePathways from '../hooks/usePathways';
import { usePortfolio } from '../hooks/usePortfolio';
import { useStatuscode } from '../hooks/useStatuscode';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const CourseComponent = () => {
  const param = useParams<{ coursekey?: string; lessonkey?: string }>();
  const courseKey = param.coursekey;

  const navigate = useNavigate();

  const { courseData, loadingCourse, loadCourseError, getCourse } =
    usePathways();

  useEffect(() => {
    if (courseKey) {
      getCourse(courseKey);
    }
  }, [courseKey]);

  const courseOrPathway = isNaN(+courseKey) ? 'pathway' : 'course';

  return loadingCourse ? (
    <CustomLoadingProgress />
  ) : courseData ? (
    courseOrPathway === 'course' ? (
      <CourseDialog pathway={courseData} open onClose={() => navigate('/')} />
    ) : (
      <PathwayDialog pathway={courseData} open onClose={() => navigate('/')} />
    )
  ) : (
    loadCourseError && (
      <Typography variant="body1" style={{ textAlign: 'center' }}>
        {loadCourseError?.graphQLErrors[0]?.message ||
          `Either you don't have access to this ${courseOrPathway} or the ${courseOrPathway} doesn't exist.`}
      </Typography>
    )
  );
};

const LessonComponent = () => {
  const navigate = useNavigate();
  const param = useParams<{ coursekey?: string; lessonkey?: string }>();
  const courseKey = param.coursekey;
  const lessonkey = +param.lessonkey;
  const query = useQuery();
  const layerString = query.has('layer');
  let layerkey: number = undefined;
  if (layerString) {
    layerkey = +query.get('layer');
  }
  const userkey = AuthService.Userkey;
  /** Parameters for launching the lesson. */
  const [launchParamsV2, setLaunchParamsV2] =
    useState<LessonLaunchParamsV2>(null);
  const { getLesson, loadingLesson, loadLessonError, lesson } = useLessons();
  const { getStatusCode } = useStatuscode();
  const { portfolio, loadingPortfolio, loadPortfolioError } = usePortfolio(
    userkey,
    lessonkey,
    layerkey
  );

  useEffect(() => {
    if (portfolio && !loadingLesson && !lesson) {
      getLesson(lessonkey);
    }
  }, [portfolio, lesson, loadingLesson]);

  useEffect(() => {
    if (lesson && portfolio) {
      const p = portfolio;
      const l = lesson;

      const lessonWindow = l.type === 'xapi' ? window.open() : null;
      const lessonStatus =
        l.type === 'xapi'
          ? null // TODO: This might be Xapi only, see useXApiLessons?
          : getStatusCode(p); //Get lesson status code to show certificate if added
      const params: LessonLaunchParamsV2 = {
        lessonWindow,
        title: l.title,
        lessonkey: p.modulekey,
        lessonVersion: p.moduleversion,
        completedDate: p.completed ? new Date(p.completed) : null,
        state: p.state,
        lessonType: l.type,
        displaySettings: l.displaySettings,
        completionType: l.behaviorSettings.completionType,
        completionThreshold: l.behaviorSettings.completionThreshold,
        layer: p.layer,
        portfolioLocked:
          p.moduleversion !== l.version.liveVersion ||
          [
            ApiPortfolioState.AssessmentSubmit,
            ApiPortfolioState.AssessmentLock,
            ApiPortfolioState.QuizLock
          ].includes(p.state),
        status: lessonStatus,
        hasCert: !!l.assets?.certificate?.media
      };
      setLaunchParamsV2(params);
    }
  }, [lesson]);

  const handleClose = () => {
    setLaunchParamsV2(null);
    navigate(`/dashboard/course/${courseKey}`);
  };

  /** Configuration for lesson player. */
  const lessonPlayerConfig: LessonPlayerContextModel = {
    preview: false,
    coursekey: +courseKey,
    lessonState: {
      answerParams: {
        userkey,
        lessonkey,
        layer: portfolio?.layer
      },
      state: portfolio?.state
    }
  };

  return !isNaN(layerkey) ? (
    <>
      {(loadingPortfolio || loadingLesson) && (
        <Grid style={{ textAlign: 'center' }}>
          <CustomLoadingProgress />
        </Grid>
      )}
      {(loadPortfolioError || loadLessonError) && (
        <Typography variant="body1" style={{ textAlign: 'center' }}>
          Either you don't have access to this lesson or the lesson doesn't
          exist.
        </Typography>
      )}

      {launchParamsV2 && (
        <LessonPlayerContextProvider value={lessonPlayerConfig}>
          <LessonPlayerEmbedded
            launchParamsV2={launchParamsV2}
            coursekey={+courseKey}
            onClose={handleClose}
            preview={false}
          />
        </LessonPlayerContextProvider>
      )}
    </>
  ) : (
    <CourseComponent />
  );
};

/**
 * Routing to handle single course or pathway opening with pathkey.
 * Routing to handle lesson load with lesson key.
 */
const CourseRoutes = () => {
  return (
    <Suspense
      fallback={
        <Box textAlign="center" pt={2}>
          <CustomLoadingProgress />
        </Box>
      }
    >
      <Routes>
        <Route index element={<UserDashboard />} />
        <Route path=":coursekey" element={<CourseComponent />} />
        <Route path=":coursekey/:lessonkey" element={<LessonComponent />} />
      </Routes>
    </Suspense>
  );
};

export default CourseRoutes;
